import React, { useEffect, useState } from 'react';
import t from '@guestyci/localize/t.macro';
import history from '@guestyci/history';
import { Stack, Text, Button, Heading } from '@guestyci/arc';
import { CircleHelp as HelpIcon } from '@guestyci/icons/system';
import { utils } from 'utils';
import { GUESTY_HELP_TROUBLESHOOTING_LOGIN_URL } from 'utils/consts';
import { reportActionToDataDog } from '../../../utils/datadog';
import { ReactComponent as BackIcon } from '../../assets/back-icon.svg';
import { ReactComponent as GuestyLogo } from '../../assets/guesty-logo-dark-blue.svg';

const isGoogleLoginByIdp = async oktaAuth => {
  try {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    return idToken?.claims?.idp?.startsWith('0oaa');
  } catch (e) {
    return false;
  }
};

const reportErrorLogin = error => {
  try {
    reportActionToDataDog(`Login Error - ${error}`, { error });
  } catch (er) {
    console.error('Error: Fail to fetch google login data and report');
  }
};

const reportSuccessLogin = ({ isGoogleLogin }) => {
  try {
    reportActionToDataDog(`Login Success - ${isGoogleLogin ? 'Google' : 'Password'}`);
  } catch (er) {
    console.error('Error: Fail log success data');
  }
};

const OktaError = ({ err }) => {
  utils.useDocumentTitle('Login');
  const errorTitle = t('Something went wrong');
  const wrongSessioneText = t('Please check if you have any Guesty tabs open. If you do, close them and try to login again');
  const googleErrorText = t(`This user hasn't been validated. Please turn to support to validate your user in order to login to Guesty`);
  const inactiveAccountText = t('You tried to login too many times. Please wait 10 minutes before trying again.');
  const tryAgainText = t('Please try again');
  const backToLginText = t('Back to login');
  const helpCenterText = t('Help center');

  const getTextByError = error => {
    if (error?.includes('User creation was disabled.')) {
      return googleErrorText;
    }
    if (error?.includes('User status is invalid')) {
      return inactiveAccountText;
    }
    if (error?.includes('Could not load PKCE codeVerifier from storage')) {
      return wrongSessioneText;
    }
    return tryAgainText;
  };

  const goToLogin = () => history.push('/login');
  const openHelpCenter = () =>
    window.open(GUESTY_HELP_TROUBLESHOOTING_LOGIN_URL, '_blank');

  const error = err?.message ? err?.message : err?.toString();
  const errorText = getTextByError(error);
  console.error('Okta Error: login failed');
  reportErrorLogin(error);

  return (
    <p>
      <Stack align="center" justify="center" className="gst-h-full">
        <GuestyLogo width={90} height={60} aria-hidden="true" />
        <Stack
          spacing={5}
          align="center"
          justify="center"
          className="gst-w-[300px] gst-h-1/2"
        >
          <Heading variant="h2">{errorTitle}</Heading>
          {errorText && (
            <Text align="center" className="gst-text-foreground">
              {errorText}
            </Text>
          )}
          <Stack direction="row" spacing={2} align="center" justify="center">
            <Button
              variant="plain"
              className="gst-text-foreground gst-font-semibold"
              onClick={goToLogin}
            >
              <Stack direction="row" spacing={1} align="center">
                <BackIcon
                  width={16}
                  height={16}
                  aria-hidden="true"
                  className="gst-shrink-0"
                />
                <Text>{backToLginText}</Text>
              </Stack>
            </Button>
            <Button
              variant="plain"
              className="gst-text-foreground gst-font-semibold gst-shadow-none"
              onClick={openHelpCenter}
            >
              <Stack direction="row" spacing={1} align="center">
                <HelpIcon
                  width={16}
                  height={16}
                  aria-hidden="true"
                  className="gst-shrink-0"
                />
                <Text>{helpCenterText}</Text>
              </Stack>
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </p>
  );
};

export default function LoginCallback({
  oktaAuth,
  authState,
  loadingElement = null,
  onAuthResume,
  errorComponent,
}) {
  const [callbackError, setCallbackError] = useState(null);

  const ErrorReporter = errorComponent || OktaError;

  const handelAuth = async () => {
    const isInteractionRequired =
      oktaAuth.idx.isInteractionRequired ||
      oktaAuth.isInteractionRequired.bind(oktaAuth);

    if (onAuthResume && isInteractionRequired()) {
      onAuthResume();
      return;
    }

    try {
      await oktaAuth.handleLoginRedirect();
      const isGoogleLogin = await isGoogleLoginByIdp(oktaAuth);
      reportSuccessLogin({ isGoogleLogin });
    } catch (error) {
      setCallbackError(error);
    }
  };

  useEffect(() => {
    handelAuth();
  }, [oktaAuth]);

  const authError = authState?.error;
  const displayError = callbackError || authError;

  if (displayError) {
    return <ErrorReporter err={displayError} />;
  }

  return loadingElement;
}
